import { TDeviceAttribute , TTimeLineAttribute , THistoryTableConfig } from "../../types";
import { TRuleConfig } from "../../types/TRuleConfig";
import { TMapAttribute } from "../../types/TMapAttribute";
import { TDeviceConfig } from "../../models/TDeviceConfig";
import { AlarmsConfig, TAlarm } from "./AlarmsConfig";
import { AttributesConfig } from "./AttributesConfig";
import { RulesConfig } from "./RulesConfig";
import { MapConfig } from "./MapConfig";
import { TimeLineConfig } from "./TimeLineConfig";
import { Logger } from "../../utils/Logger";
import { IAnyObject } from "../../interfaces";
import { emptyHistoryTAbleConfig } from "../../types/THistoryTableConfig";

export class DeviceConfig {
	private static readonly logger = new Logger("DeviceConfig");
	private static instance: DeviceConfig;

	private EnvirotainerAttributesConfig: Map<string, TDeviceAttribute>;
	private ETechAttributesConfig: Map<string, TDeviceAttribute>;
	private PT300AttributesConfig: Map<string, TDeviceAttribute>;
	private EnvirotainerRulesConfig: Map<string, TRuleConfig>;
	private ETechRulesConfig: Map<string, TRuleConfig>;
	private PT300RulesConfig: Map<string, TRuleConfig>;
	private PT300MapConfig: TMapAttribute;
	private EnvirotainerMapConfig: TMapAttribute;
	private ETechMapConfig: TMapAttribute;
	private TimeLineConfig: TTimeLineAttribute;
	private isTimeLineInChart: boolean;
	private PT300HistoryTableConfig: THistoryTableConfig;
	private EnvirotainerHistoryTableConfig: THistoryTableConfig;
	private ETechHistoryTableConfig: THistoryTableConfig;
	private isTimeLineClear: boolean;
	public company: string;
	private PT300AlarmsConfig: TDeviceConfig;
	private EnvirotainerAlarmsConfig: TDeviceConfig;

	private constructor() {
	}

	public static getInstance(): DeviceConfig {
		if (!DeviceConfig.instance) {
			DeviceConfig.instance = new DeviceConfig();
		}

		return DeviceConfig.instance;
	}

	public init(config: IAnyObject): boolean {
		if (DeviceConfig.getInstance().EnvirotainerAttributesConfig && DeviceConfig.getInstance().PT300AttributesConfig) {
			return true;
		}
		try {
			DeviceConfig.getInstance().company = config.company as string;
			// Envirotainer
			const envirotainer = config["ENVIROTAINER"] as IAnyObject;
			DeviceConfig.getInstance().EnvirotainerAttributesConfig = new AttributesConfig(envirotainer["attributesConfig"] as {[key: string]: {[key: string]: string}}).getAttributesConfig();
			DeviceConfig.getInstance().EnvirotainerRulesConfig = new RulesConfig(envirotainer["rulesConfig"] as {[key: string]: {[key: string]: string | number | boolean}}).getRulesConfig();
			DeviceConfig.getInstance().EnvirotainerMapConfig = new MapConfig(config.company as string, (envirotainer["mapConfig"] as IAnyObject)["popupAttributes"] as string[]).getMapConfig();
			DeviceConfig.getInstance().EnvirotainerHistoryTableConfig = emptyHistoryTAbleConfig;
			DeviceConfig.getInstance().EnvirotainerAlarmsConfig = new AlarmsConfig(envirotainer["alarmConfig"] as TAlarm[]).getAlarmsConfig();
			// E-Tech
			const eTECH = config["E-TECH"] as IAnyObject;
			DeviceConfig.getInstance().ETechAttributesConfig = new AttributesConfig(eTECH["attributesConfig"] as {[key: string]: {[key: string]: string}}).getAttributesConfig();
			DeviceConfig.getInstance().ETechRulesConfig = new RulesConfig(eTECH["rulesConfig"] as {[key: string]: {[key: string]: string | number | boolean}}).getRulesConfig();
			DeviceConfig.getInstance().ETechMapConfig = new MapConfig(config.company as string, (eTECH["mapConfig"] as IAnyObject)["popupAttributes"] as string[]).getMapConfig();
			DeviceConfig.getInstance().ETechHistoryTableConfig = emptyHistoryTAbleConfig;
			// PT300D
			const pt300d = config["PT300D"] as IAnyObject;
			DeviceConfig.getInstance().PT300AttributesConfig = new AttributesConfig(pt300d["attributesConfig"] as {[key: string]: {[key: string]: string}}).getAttributesConfig();
			DeviceConfig.getInstance().PT300RulesConfig = new RulesConfig(pt300d["rulesConfig"] as {[key: string]: {[key: string]: string | number | boolean}}).getRulesConfig();
			DeviceConfig.getInstance().PT300MapConfig = new MapConfig(config.company as string, (pt300d["mapConfig"] as IAnyObject)["popupAttributes"] as string[]).getMapConfig();
			DeviceConfig.getInstance().PT300HistoryTableConfig = pt300d["historyTableConfig"] as THistoryTableConfig ?? emptyHistoryTAbleConfig;
			DeviceConfig.getInstance().PT300AlarmsConfig = new AlarmsConfig(pt300d["alarmConfig"] as TAlarm[]).getAlarmsConfig();
			// Common by company
			DeviceConfig.getInstance().TimeLineConfig = new TimeLineConfig(config["timeLineConfig"] as {origin: string; destination: string; isShowEmptyMilestones: boolean}, config.company as string).getTimeLineConfig();
			DeviceConfig.getInstance().isTimeLineInChart = !!config.isTimeLineInChart;
			DeviceConfig.getInstance().isTimeLineClear = !!config.isTimeLineClear;

			return true;
		} catch (error) {
			DeviceConfig.logger.error("DeviceConfig >>", error);

			return false;
		}
	}

	public static getEnvirotainerAttributesConfig(): Map<string, TDeviceAttribute> {
		return DeviceConfig.getInstance().EnvirotainerAttributesConfig;
	}

	public static getEnvirotainerRulesConfig(): Map<string, TRuleConfig> {
		return DeviceConfig.getInstance().EnvirotainerRulesConfig;
	}

	public static getEnvirotainerMapConfig(): TMapAttribute {
		return DeviceConfig.getInstance().EnvirotainerMapConfig;
	}

	public static getEnvirotainerHistoryTableConfig(): THistoryTableConfig {
		return DeviceConfig.getInstance().EnvirotainerHistoryTableConfig;
	}

	public static getEnvirotainerAlarmsConfig(): TDeviceConfig {
		return DeviceConfig.getInstance().EnvirotainerAlarmsConfig;
	}

	public static getETechAttributesConfig(): Map<string, TDeviceAttribute> {
		return DeviceConfig.getInstance().ETechAttributesConfig;
	}

	public static getETechRulesConfig(): Map<string, TRuleConfig> {
		return DeviceConfig.getInstance().ETechRulesConfig;
	}

	public static getETechMapConfig(): TMapAttribute {
		return DeviceConfig.getInstance().ETechMapConfig;
	}

	public static getETechHistoryTableConfig(): THistoryTableConfig {
		return DeviceConfig.getInstance().ETechHistoryTableConfig;
	}

	public static getPT300AttributesConfig(): Map<string, TDeviceAttribute> {
		return DeviceConfig.getInstance().PT300AttributesConfig;
	}

	public static getPT300RulesConfig(): Map<string, TRuleConfig> {
		return DeviceConfig.getInstance().PT300RulesConfig;
	}

	public static getPT300MapConfig(): TMapAttribute {
		return DeviceConfig.getInstance().PT300MapConfig;
	}

	public static getPT300HistoryTableConfig(): THistoryTableConfig {
		return DeviceConfig.getInstance().PT300HistoryTableConfig;
	}

	public static getPT300AlarmsConfig(): TDeviceConfig {
		return DeviceConfig.getInstance().PT300AlarmsConfig;
	}

	public static getTimeLineConfig(): TTimeLineAttribute {
		return DeviceConfig.getInstance().TimeLineConfig;
	}

	public static getIsTimeLineInChart(): boolean {
		return DeviceConfig.getInstance().isTimeLineInChart;
	}

	public static getIsTimeLineClear(): boolean {
		return DeviceConfig.getInstance().isTimeLineClear;
	}
}
